import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useCurrencyStore = defineStore('currency', () => {
    const currency = ref(localStorage.getItem('storefront-currency') || 'MKD')
    const baseCurrency = 'MKD'
    const rates = ref<Record<string, number>>({})
    const isLoading = ref(false)
    const availableCurrencies = [
        { code: 'MKD', symbol: 'ден', name: 'MKD' },
        { code: 'EUR', symbol: '€', name: 'EUR' },
        { code: 'USD', symbol: '$', name: 'USD' }
    ]

    const currentSymbol = computed(() => {
        return availableCurrencies.find(c => c.code === currency.value)?.symbol || ''
    })

    async function fetchRates() {
        // Return early if we already have rates (simple cache)
        if (Object.keys(rates.value).length > 0) return

        isLoading.value = true
        try {
            // Using a free API endpoint (Open Exchange Rates API - no key needed for this endpoint)
            const response = await fetch('https://open.er-api.com/v6/latest/MKD')
            const data = await response.json()

            if (data && data.rates) {
                rates.value = data.rates
            }
        } catch (error) {
            console.error('Failed to fetch currency rates:', error)
            // Fallback rates if API fails (approximate values)
            rates.value = {
                MKD: 1,
                EUR: 0.0162, // ~61.5 MKD
                USD: 0.0175  // ~57 MKD
            }
        } finally {
            isLoading.value = false
        }
    }

    function setCurrency(code: string) {
        if (availableCurrencies.some(c => c.code === code)) {
            currency.value = code
            localStorage.setItem('storefront-currency', code)
            // Ensure rates are fetched if changing to non-base
            if (code !== baseCurrency) {
                fetchRates()
            }
        }
    }

    function convert(amount: number): number {
        if (currency.value === baseCurrency) return amount
        const rate = rates.value[currency.value]
        if (!rate) {
            // Should fetch if not available, but for sync return, fallback to 1 or previous logic
            // If rates haven't loaded yet, return amount (or 0?) - ideally UI handles loading
            fetchRates() // Trigger fetch if missing
            return amount
        }
        return amount * rate
    }

    function format(amount: number): string {
        const converted = convert(amount)

        // Formatting options
        const options: Intl.NumberFormatOptions = {
            style: 'currency',
            currency: currency.value,
            minimumFractionDigits: currency.value === 'MKD' ? 0 : 2,
            maximumFractionDigits: currency.value === 'MKD' ? 0 : 2,
        }

        try {
            // Some browsers might not support all currency codes with default locale
            // We use a generic 'en-US' or detect locale, but 'mk-MK' usually formats MKD as 1.200 ден
            const locale = currency.value === 'MKD' ? 'mk-MK' : 'en-IE' // Ireland uses EUR and English
            return new Intl.NumberFormat(locale, options).format(converted)
        } catch (e) {
            // Fallback formatting
            return `${converted.toFixed(2)} ${currentSymbol.value}`
        }
    }

    return {
        currency,
        rates,
        availableCurrencies,
        isLoading,
        setCurrency,
        format,
        fetchRates
    }
})
